import React from 'react';
import './Terms.css';

const Terms = () => {
  return (
    <div className="terms-page">
      {/* HERO */}
      <section className="legal-hero">
        <div className="container">
          <div className="legal-header">
            <div className="section-tag">Legal</div>
            <h1>Terms & Conditions</h1>
            <p>Governing the use of Krishpar Technologies HR services and platform</p>
          </div>
        </div>
      </section>

      {/* META INFO */}
      <section className="meta-section">
        <div className="container">
          <div className="meta-bar">
            <span><strong>Effective Date:</strong> 01 January 2025</span>
            <span><strong>Last Updated:</strong> 15 April 2025</span>
            <span><strong>Governing Law:</strong> India</span>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="legal-content">
        <div className="container">

          {/* TABLE OF CONTENTS */}
          <div className="toc">
            <h3>Table of Contents</h3>
            <ol>
              <li><a href="#t1">Acceptance of Terms</a></li>
              <li><a href="#t2">Services Offered</a></li>
              <li><a href="#t3">Client Obligations</a></li>
              <li><a href="#t4">Candidate Obligations</a></li>
              <li><a href="#t5">Fees & Payment</a></li>
              <li><a href="#t6">Confidentiality</a></li>
              <li><a href="#t7">Intellectual Property</a></li>
              <li><a href="#t8">Limitation of Liability</a></li>
              <li><a href="#t9">Indemnification</a></li>
              <li><a href="#t10">Termination</a></li>
              <li><a href="#t11">Dispute Resolution</a></li>
              <li><a href="#t12">Governing Law & Jurisdiction</a></li>
            </ol>
          </div>

          <div className="legal-intro">
            <p>These Terms and Conditions ("Terms") govern the use of services provided by <strong>Krishpar Technologies</strong> ("Company", "we", "us"), an HR services company registered in India. By engaging our services or accessing our website, you ("Client", "Candidate", or "User") agree to be bound by these Terms.</p>
          </div>

          {/* SECTIONS */}
          <div className="section" id="t1">
            <div className="section-header"><div className="section-num">1</div><h2>Acceptance of Terms</h2></div>
            <p>By accessing our website, signing a service agreement, or engaging with our HR services, you acknowledge that you have read, understood, and agree to these Terms. If you do not agree, please refrain from using our services.</p>
            <p>These Terms may be updated periodically. Continued use of our services after any changes constitutes acceptance of the updated Terms.</p>
          </div>

          <div className="section" id="t2">
            <div className="section-header"><div className="section-num">2</div><h2>Services Offered</h2></div>
            <p>Krishpar Technologies provides the following HR services:</p>
            <ul>
              <li>Permanent and contractual recruitment & staffing solutions.</li>
              <li>Payroll processing, compliance management (PF, ESI, TDS, PT), and payslip generation.</li>
              <li>Background verification and pre-employment screening.</li>
              <li>HR outsourcing — including attendance management, leave administration, and employee lifecycle management.</li>
              <li>Training & development, induction programs, and HR policy consulting.</li>
              <li>POSH (Prevention of Sexual Harassment) compliance and Internal Complaints Committee (ICC) support.</li>
            </ul>
            <div className="highlight">
              <strong>Scope of Services:</strong> The exact scope, deliverables, and timelines for each engagement are defined in the respective Service Agreement or Work Order signed between the parties.
            </div>
          </div>

          <div className="section" id="t3">
            <div className="section-header"><div className="section-num">3</div><h2>Client Obligations</h2></div>
            <p>Clients engaging Krishpar Technologies agree to:</p>
            <ul>
              <li>Provide accurate job descriptions, salary bands, and requirements in a timely manner.</li>
              <li>Maintain confidentiality of candidate profiles shared by us and not share them with third parties without our consent.</li>
              <li>Pay all applicable fees as per the agreed fee schedule within stipulated timelines.</li>
              <li>Ensure that their internal processes comply with applicable labour laws and notify us of any regulatory changes that affect the services.</li>
              <li>Designate a point of contact for smooth service delivery and communication.</li>
              <li>Not directly engage or poach candidates introduced by us during the contract period and for 12 months thereafter without paying the applicable placement fee.</li>
            </ul>
          </div>

          <div className="section" id="t4">
            <div className="section-header"><div className="section-num">4</div><h2>Candidate Obligations</h2></div>
            <p>Individuals applying for positions through Krishpar Technologies agree to:</p>
            <ul>
              <li>Provide accurate and truthful information in their resume, applications, and during interviews.</li>
              <li>Consent to background verification as required by client companies.</li>
              <li>Not misrepresent qualifications, experience, or identity in any form.</li>
              <li>Inform us promptly of any change in employment status, contact details, or interest in a role.</li>
            </ul>
            <p>Misrepresentation of information may result in immediate disqualification and reporting to relevant authorities.</p>
          </div>

          <div className="section" id="t5">
            <div className="section-header"><div className="section-num">5</div><h2>Fees & Payment</h2></div>
            <p>Fees for our services are governed by individual Service Agreements. General terms:</p>
            <ul>
              <li><strong>Recruitment Fees:</strong> Typically a percentage of the candidate's first annual CTC, payable upon the candidate joining.</li>
              <li><strong>Replacement Guarantee:</strong> A free replacement is offered within the warranty period specified in the agreement, subject to conditions.</li>
              <li><strong>Payroll/HR Outsourcing:</strong> Monthly retainer fees as agreed. Invoices are due within 15 days of issuance.</li>
              <li><strong>Late Payments:</strong> A late fee of 1.5% per month may be charged on overdue amounts.</li>
              <li><strong>GST:</strong> All fees are exclusive of applicable Goods & Services Tax (GST), which will be charged at the prevailing rate.</li>
            </ul>
          </div>

          <div className="section" id="t6">
            <div className="section-header"><div className="section-num">6</div><h2>Confidentiality</h2></div>
            <p>Both parties agree to maintain strict confidentiality of:</p>
            <ul>
              <li>Candidate profiles, compensation details, and personal data.</li>
              <li>Client business information, HR strategies, and employee data.</li>
              <li>Proprietary processes, tools, and methodologies used by Krishpar Technologies.</li>
            </ul>
            <p>Confidentiality obligations survive the termination of any service agreement for a period of <strong>3 years</strong>.</p>
          </div>

          <div className="section" id="t7">
            <div className="section-header"><div className="section-num">7</div><h2>Intellectual Property</h2></div>
            <p>All content on the Krishpar Technologies website — including text, graphics, logos, software, and proprietary HR frameworks — is the intellectual property of the Company and is protected under applicable Indian IP laws.</p>
            <p>Clients are granted a limited, non-transferable licence to use deliverables (reports, templates, policies) created exclusively for them under their engagement. No content may be reproduced or distributed without prior written consent.</p>
          </div>

          <div className="section" id="t8">
            <div className="section-header"><div className="section-num">8</div><h2>Limitation of Liability</h2></div>
            <p>To the maximum extent permitted by law:</p>
            <ul>
              <li>Krishpar Technologies is not liable for any indirect, incidental, or consequential loss arising from the use or inability to use our services.</li>
              <li>Our total liability for any claim shall not exceed the fees paid for the specific service giving rise to the claim, in the preceding 3 months.</li>
              <li>We are not responsible for decisions made by clients based on our recommendations; final hiring, HR, or business decisions remain the sole responsibility of the client.</li>
            </ul>
          </div>

          <div className="section" id="t9">
            <div className="section-header"><div className="section-num">9</div><h2>Indemnification</h2></div>
            <p>You agree to indemnify and hold Krishpar Technologies, its directors, employees, and agents harmless from any claims, losses, damages, or expenses (including legal fees) arising out of:</p>
            <ul>
              <li>Your breach of these Terms or any applicable law.</li>
              <li>Misuse of our services or misrepresentation of information.</li>
              <li>Any third-party claims arising from actions you take based on our services.</li>
            </ul>
          </div>

          <div className="section" id="t10">
            <div className="section-header"><div className="section-num">10</div><h2>Termination</h2></div>
            <p>Either party may terminate a service agreement by providing <strong>30 days' written notice</strong>, unless otherwise specified in the agreement. Krishpar Technologies reserves the right to terminate services immediately in case of:</p>
            <ul>
              <li>Non-payment of dues beyond the grace period.</li>
              <li>Material breach of these Terms by the client or candidate.</li>
              <li>Illegal, unethical, or abusive conduct towards our staff.</li>
            </ul>
            <p>Upon termination, all outstanding fees become immediately payable and both parties shall return or destroy confidential information as instructed.</p>
          </div>

          <div className="section" id="t11">
            <div className="section-header"><div className="section-num">11</div><h2>Dispute Resolution</h2></div>
            <p>In the event of any dispute arising out of or in connection with these Terms or any service agreement:</p>
            <ul>
              <li><strong>Step 1 — Negotiation:</strong> Parties will attempt good-faith resolution within 30 days of written notice of dispute.</li>
              <li><strong>Step 2 — Mediation:</strong> If unresolved, parties may opt for mediation under the Mediation Act, 2023.</li>
              <li><strong>Step 3 — Arbitration:</strong> Disputes unresolved through mediation shall be referred to arbitration under the Arbitration and Conciliation Act, 1996, with a sole arbitrator mutually agreed upon.</li>
            </ul>
          </div>

          <div className="section" id="t12">
            <div className="section-header"><div className="section-num">12</div><h2>Governing Law & Jurisdiction</h2></div>
            <p>These Terms are governed by the laws of India. Any legal proceedings shall be subject to the exclusive jurisdiction of the courts of <strong>Chennai, Tamil Nadu</strong>.</p>
          </div>

          {/* CONTACT */}
          <div className="contact-section-terms">
            <div className="contact-card-terms">
              <h3>Questions About These Terms?</h3>
              <p>We're here to help. Reach out to our legal and compliance team for any queries about our Terms & Conditions.</p>
              <div className="contact-grid">
                <div className="contact-item">
                  <div className="label">Email</div>
                  <a href="mailto:info@krishpar.com">info@krishpar.com</a>
                </div>
                <div className="contact-item">
                  <div className="label">Phone</div>
                  <a href="tel:+918925516133">+91 89255 16133</a>
                </div>
                <div className="contact-item">
                  <div className="label">Address</div>
                  <span>Chennai, Tamil Nadu, India</span>
                </div>
                <div className="contact-item">
                  <div className="label">Response Time</div>
                  <span>Within 30 business days</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
